/**
 * Distance calculation utilities for GPS coordinates
 */

import { GpsPoint } from '../domain/telemetry/processed/GpsPoint'

/**
 * Calculates the distance between two GPS coordinates using the Haversine formula
 * @param lat1 Latitude of first point in decimal degrees
 * @param lon1 Longitude of first point in decimal degrees
 * @param lat2 Latitude of second point in decimal degrees
 * @param lon2 Longitude of second point in decimal degrees
 * @returns Distance in meters
 */
export function calculateDistance(
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number
): number {
  const R = 6371e3 // Earth's radius in meters
  const φ1 = (lat1 * Math.PI) / 180
  const φ2 = (lat2 * Math.PI) / 180
  const Δφ = ((lat2 - lat1) * Math.PI) / 180
  const Δλ = ((lon2 - lon1) * Math.PI) / 180

  const a =
    Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
    Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2)
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))

  return R * c
}

/**
 * Calculates distance between two telemetry points
 */
export function calculatePointDistance(
  point1: GpsPoint,
  point2: GpsPoint
): number {
  return calculateDistance(
    point1.latitude,
    point1.longitude,
    point2.latitude,
    point2.longitude
  )
}

/**
 * Rounds distance to nearest interval (for indexing)
 * @param distance Distance in meters
 * @param interval Rounding interval in meters (default 5)
 * @returns Rounded distance
 */
export function roundDistance(distance: number, interval: number = 5): number {
  return Math.round(distance / interval) * interval
}

/**
 * Nearest item to `targetDistance` by a caller-supplied distance selector —
 * full linear scan, first-minimal wins on ties. The one nearest-point snap
 * shared by both clients' zone markers (web recalibrated points, mobile GPS
 * points), so tie-breaking can never drift between them.
 * @returns The closest item, or undefined for an empty array
 */
export function findClosestByDistance<T>(
  items: readonly T[],
  targetDistance: number,
  getDistance: (item: T) => number
): T | undefined {
  let closest: T | undefined
  let bestDiff = Infinity
  for (const item of items) {
    const diff = Math.abs(getDistance(item) - targetDistance)
    if (diff < bestDiff) {
      bestDiff = diff
      closest = item
    }
  }
  return closest
}

/**
 * Finds the closest point in a sorted array of telemetry points
 * @param targetDistance Target distance to find
 * @param points Array of telemetry points sorted by distance
 * @param lastIndex Last found index (optimization hint)
 * @param searchWindow Search window size
 * @returns Object containing closest point and its index
 */
export function findClosestPointByDistance(
  targetDistance: number,
  points: GpsPoint[],
  lastIndex: number = 0,
  searchWindow: number = 50
): { point: GpsPoint; index: number } {
  const startIndex = Math.max(0, lastIndex)
  const endIndex = Math.min(points.length, startIndex + searchWindow)

  let closestPoint = points[startIndex]
  let smallestDiff = Math.abs(
    points[startIndex].distanceMeters - targetDistance
  )
  let closestIndex = startIndex

  for (let i = startIndex + 1; i < endIndex; i++) {
    const diff = Math.abs(points[i].distanceMeters - targetDistance)
    if (diff < smallestDiff) {
      smallestDiff = diff
      closestPoint = points[i]
      closestIndex = i
    }
    // If we're past the target and difference is increasing, we can stop
    if (points[i].distanceMeters > targetDistance && diff > smallestDiff) {
      break
    }
  }

  return { point: closestPoint, index: closestIndex }
}

/**
 * Recalibrates distance values from one lap to match another lap's distance scale
 * This is essential for comparing laps that may have slightly different GPS measurements
 *
 * @param sourcePoints Points to recalibrate
 * @param referencePoints Reference lap points
 * @returns Recalibrated points with distances matched to reference lap
 */
export function recalibrateDistances(
  sourcePoints: GpsPoint[],
  referencePoints: GpsPoint[]
): GpsPoint[] {
  const recalibrate = createDistanceRecalibrator(referencePoints)
  let lastRefIndex = 0

  return sourcePoints.map((sourcePoint) => {
    const { recalibratedDistance, newIndex } = recalibrate(
      sourcePoint,
      lastRefIndex
    )
    lastRefIndex = newIndex
    return { ...sourcePoint, distanceMeters: recalibratedDistance }
  })
}

/**
 * Fast squared distance calculation for comparing distances (avoids sqrt).
 * Uses equirectangular approximation which is accurate for small distances.
 * Much faster than Haversine for comparison purposes.
 */
function fastSquaredDistance(
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number
): number {
  // Equirectangular approximation - fast and accurate for small distances
  const x = (lon2 - lon1) * Math.cos((lat1 + lat2) * 0.00872664626) // 0.5 * PI/180
  const y = lat2 - lat1
  return x * x + y * y
}

/**
 * Distance along the reference lap of the target's foot on the reference
 * polyline: the target is projected onto whichever of the two segments
 * meeting at the nearest fix `index` it lies closest to, and the reference
 * distance is interpolated along that segment (clamped to its ends). A lap
 * sampled denser than the reference lands between the reference fixes
 * instead of on them; a target beyond the last fix lands on it.
 */
function distanceAlongReference(
  tLat: number,
  tLon: number,
  index: number,
  refLats: number[],
  refLons: number[],
  refDistances: number[]
): number {
  const cosLat = Math.cos(tLat * 0.01745329252)
  const ax = (refLons[index] - tLon) * cosLat
  const ay = refLats[index] - tLat
  let best = refDistances[index]
  let bestPerpSq = Infinity
  const consider = (neighbour: number) => {
    if (neighbour < 0 || neighbour >= refLats.length) return
    const sx = (refLons[neighbour] - tLon) * cosLat - ax
    const sy = refLats[neighbour] - tLat - ay
    const len2 = sx * sx + sy * sy
    if (len2 === 0) return
    const u = Math.min(1, Math.max(0, -(ax * sx + ay * sy) / len2))
    const fx = ax + u * sx
    const fy = ay + u * sy
    const perpSq = fx * fx + fy * fy
    if (perpSq < bestPerpSq) {
      bestPerpSq = perpSq
      best =
        refDistances[index] +
        u * (refDistances[neighbour] - refDistances[index])
    }
  }
  consider(index - 1)
  consider(index + 1)
  return best
}

/**
 * Finds closest point by geographic proximity using fast squared distance.
 * OPTIMIZED: Uses equirectangular approximation and early termination.
 */
export function findClosestPointByGeo(
  target: GpsPoint,
  points: GpsPoint[],
  startIndex: number = 0,
  searchWindow: number = 100
): { point: GpsPoint; index: number } {
  const start = Math.max(0, startIndex)
  const end = Math.min(points.length, start + searchWindow)

  if (start >= points.length) {
    return { point: points[points.length - 1], index: points.length - 1 }
  }

  const targetLat = target.latitude
  const targetLon = target.longitude

  let closestPoint = points[start]
  let smallestDistSq = fastSquaredDistance(
    targetLat,
    targetLon,
    closestPoint.latitude,
    closestPoint.longitude
  )
  let closestIndex = start

  // If we're very close already (within ~1m), return early
  // 1m ≈ 0.00001 degrees, squared = 1e-10
  if (smallestDistSq < 1e-10) {
    return { point: closestPoint, index: closestIndex }
  }

  for (let i = start + 1; i < end; i++) {
    const p = points[i]
    const distSq = fastSquaredDistance(
      targetLat,
      targetLon,
      p.latitude,
      p.longitude
    )

    if (distSq < smallestDistSq) {
      smallestDistSq = distSq
      closestPoint = p
      closestIndex = i

      // Early termination if very close
      if (distSq < 1e-10) break
    }
  }

  return { point: closestPoint, index: closestIndex }
}

/**
 * Result of recalibrating a single GPS point
 */
export interface RecalibratedPoint {
  /** The original GPS point */
  originalPoint: GpsPoint
  /** Distance recalibrated to match reference lap */
  recalibratedDistance: number
  /** Original distance before recalibration */
  originalDistance: number
}

/**
 * Create a distance recalibration function for aligning laps across different sessions.
 *
 * When comparing laps from different sessions, GPS measurements may vary slightly,
 * causing the same physical location on the track to have different distance values.
 * This function creates a lookup that maps each point to its geographically closest
 * point on the reference lap, using the reference lap's distance scale.
 *
 * @param referencePoints - GPS points from the reference lap (typically the fastest)
 * @returns A function that recalibrates distances for any target lap's points
 *
 * @example
 * ```typescript
 * const recalibrate = createDistanceRecalibrator(referenceLap.getGpsPoints())
 * const targetDataPoints = targetLap.getGpsPoints().map(point => {
 *   const recalibrated = recalibrate(point)
 *   return {
 *     distance: recalibrated.recalibratedDistance,
 *     speed: point.speedKmh,
 *     // ... other fields
 *   }
 * })
 * ```
 */
export function createDistanceRecalibrator(
  referencePoints: GpsPoint[]
): (
  targetPoint: GpsPoint,
  lastIndex?: number
) => { recalibratedDistance: number; newIndex: number } {
  const refLats = referencePoints.map((p) => p.latitude)
  const refLons = referencePoints.map((p) => p.longitude)
  const refDistances = referencePoints.map((p) => p.distanceMeters)
  return (targetPoint: GpsPoint, lastIndex: number = 0) => {
    const { index: newIndex } = findClosestPointByGeo(
      targetPoint,
      referencePoints,
      lastIndex,
      100 // Search window
    )
    return {
      recalibratedDistance: distanceAlongReference(
        targetPoint.latitude,
        targetPoint.longitude,
        newIndex,
        refLats,
        refLons,
        refDistances
      ),
      newIndex,
    }
  }
}

/**
 * OPTIMIZED: Batch recalibrate all points from a target lap in a single pass.
 * This is much faster than calling createDistanceRecalibrator per-point because:
 * 1. No function call overhead per point
 * 2. Reference points array is accessed directly
 * 3. Coordinates are cached in local variables
 *
 * @param targetPoints - GPS points from the lap to recalibrate
 * @param referencePoints - GPS points from the reference lap
 * @returns Array of recalibrated distances (same order as targetPoints)
 */
export function batchRecalibrateDistances(
  targetPoints: GpsPoint[],
  referencePoints: GpsPoint[]
): number[] {
  if (referencePoints.length === 0) {
    return targetPoints.map((p) => p.distanceMeters)
  }

  const result: number[] = new Array(targetPoints.length)
  let lastIndex = 0
  const searchWindow = 100
  const refLength = referencePoints.length
  const refLats = referencePoints.map((p) => p.latitude)
  const refLons = referencePoints.map((p) => p.longitude)
  const refDistances = referencePoints.map((p) => p.distanceMeters)

  for (let t = 0; t < targetPoints.length; t++) {
    const target = targetPoints[t]
    const targetLat = target.latitude
    const targetLon = target.longitude

    // Find closest reference point using sliding window
    const start = Math.max(0, lastIndex - 20) // Allow some backward search
    const end = Math.min(refLength, start + searchWindow)

    let closestIndex = start
    let closestRef = referencePoints[start]
    let smallestDistSq = fastSquaredDistance(
      targetLat,
      targetLon,
      closestRef.latitude,
      closestRef.longitude
    )

    for (let i = start + 1; i < end; i++) {
      const ref = referencePoints[i]
      const distSq = fastSquaredDistance(
        targetLat,
        targetLon,
        ref.latitude,
        ref.longitude
      )

      if (distSq < smallestDistSq) {
        smallestDistSq = distSq
        closestRef = ref
        closestIndex = i

        // Early termination if very close (~1m)
        if (distSq < 1e-10) break
      }
    }

    result[t] = distanceAlongReference(
      targetLat,
      targetLon,
      closestIndex,
      refLats,
      refLons,
      refDistances
    )
    lastIndex = closestIndex
  }

  return result
}

/**
 * ULTRA-OPTIMIZED: Batch recalibrate using raw coordinate arrays.
 * Avoids all object property access overhead by working with flat arrays.
 *
 * @param targetLats - Array of target latitudes
 * @param targetLons - Array of target longitudes
 * @param refLats - Array of reference latitudes
 * @param refLons - Array of reference longitudes
 * @param refDistances - Array of reference distances
 * @returns Array of recalibrated distances
 */
export function batchRecalibrateRaw(
  targetLats: number[],
  targetLons: number[],
  refLats: number[],
  refLons: number[],
  refDistances: number[]
): number[] {
  const targetLen = targetLats.length
  const refLen = refLats.length

  if (refLen === 0) {
    return new Array(targetLen).fill(0)
  }

  const result = new Array<number>(targetLen)
  let lastIndex = 0
  const searchWindow = 100
  // A physically close reference point can belong to a different part of the
  // track (the other leg of a hairpin), and matching it makes recalibrated
  // distances jump by the along-track separation of the legs. Candidates are
  // therefore restricted to a corridor around the expected progress (previous
  // match + GPS step); the corridor is wide enough for GPS noise and line
  // choice but far smaller than any leg-to-leg distance.
  const corridorBehindM = 50
  const corridorAheadM = 15
  const degToMeters = 111320
  // Corridor anchor: the last UNCLAMPED matched distance. Anchoring on the
  // monotonically clamped output instead would freeze the corridor after an
  // accepted forward mismatch and matching could never re-anchor.
  let anchor: number | undefined
  // Target distance travelled since `anchor` last moved. While a match is
  // stuck on the same reference point — e.g. the target is crossing a
  // reference GPS dropout whose first post-gap point lies beyond the fixed
  // forward corridor — this keeps growing so the expected-progress corridor
  // widens forward and re-acquires the reference, instead of freezing the
  // recalibrated distance (and flat-lining the chart) for the rest of the lap.
  let travelSinceAnchor = 0

  // PERF: Reference travel directions don't depend on the target point, so
  // they are precomputed once (O(refLen)) instead of per candidate in the
  // hot loop (the dot product against the target's step direction is all
  // that remains there).
  const refDirLonM = new Array<number>(refLen)
  const refDirLatM = new Array<number>(refLen)
  for (let i = 0; i < refLen; i++) {
    const next = i + 1 < refLen ? i + 1 : i
    const prev = next === i ? Math.max(0, i - 1) : i
    refDirLonM[i] =
      (refLons[next] - refLons[prev]) *
      Math.cos((refLats[next] + refLats[prev]) * 0.00872664626)
    refDirLatM[i] = refLats[next] - refLats[prev]
  }

  for (let t = 0; t < targetLen; t++) {
    const tLat = targetLats[t]
    const tLon = targetLons[t]

    // Sliding window search
    const start = Math.max(0, lastIndex - 20)
    const end = Math.min(refLen, start + searchWindow)

    // Expected along-track progress for the corridor check (skipped for the
    // very first point, which has no anchor yet)
    let minRefDist = -Infinity
    let maxRefDist = Infinity
    // Target travel direction, for rejecting candidates on opposite-direction
    // track sections (e.g. the other leg of a hairpin, which can be the
    // geometrically NEAREST line when the riders' line choices differ)
    let stepLonM = 0
    let stepLatM = 0
    let hasHeading = false
    if (t > 0 && anchor !== undefined) {
      stepLonM =
        (tLon - targetLons[t - 1]) *
        Math.cos((tLat + targetLats[t - 1]) * 0.00872664626)
      stepLatM = tLat - targetLats[t - 1]
      const stepMeters =
        Math.sqrt(stepLonM * stepLonM + stepLatM * stepLatM) * degToMeters
      // Below ~0.5m of movement, GPS noise dominates the heading
      hasHeading = stepMeters > 0.5
      travelSinceAnchor += stepMeters
      // Expected progress measures from the anchor by how far the TARGET has
      // travelled since the anchor last moved (one step in the normal case),
      // so a stuck match widens the corridor forward each point.
      const expected = anchor + travelSinceAnchor
      minRefDist = expected - corridorBehindM
      maxRefDist = expected + corridorAheadM
    }

    let closestIndex = -1
    let smallestDistSq = Infinity

    for (let i = start; i < end; i++) {
      // PERF: The corridor and heading checks run BEFORE the squared-distance
      // math — they are cheap compares and reject most of the window, so the
      // common path does less work per candidate than an unconditional
      // distance computation would.
      const refDist = refDistances[i]
      if (refDist < minRefDist || refDist > maxRefDist) continue

      // Reject candidates whose local track direction opposes the target's
      // travel direction (same scaling on both vectors keeps the dot
      // product's sign meaningful)
      if (
        hasHeading &&
        refDirLonM[i] * stepLonM + refDirLatM[i] * stepLatM < 0
      ) {
        continue
      }

      // Inline fast squared distance calculation
      const dx =
        (refLons[i] - tLon) * Math.cos((tLat + refLats[i]) * 0.00872664626)
      const dy = refLats[i] - tLat
      const distSq = dx * dx + dy * dy

      if (distSq < smallestDistSq) {
        smallestDistSq = distSq
        closestIndex = i
        if (distSq < 1e-10) break
      }
    }

    if (closestIndex === -1) {
      // Nothing inside the corridor (reference data gap or a lost match) —
      // rare recovery pass: fall back to the geometric nearest in the window
      // so matching can re-anchor.
      let nearestAnyIndex = start
      let nearestAnyDistSq = Infinity
      for (let i = start; i < end; i++) {
        const dx =
          (refLons[i] - tLon) * Math.cos((tLat + refLats[i]) * 0.00872664626)
        const dy = refLats[i] - tLat
        const distSq = dx * dx + dy * dy
        if (distSq < nearestAnyDistSq) {
          nearestAnyDistSq = distSq
          nearestAnyIndex = i
        }
      }
      anchor = refDistances[nearestAnyIndex]
      travelSinceAnchor = 0
      result[t] = distanceAlongReference(
        tLat,
        tLon,
        nearestAnyIndex,
        refLats,
        refLons,
        refDistances
      )
      lastIndex = nearestAnyIndex
    } else {
      // Clamp residual backward jitter (e.g. crossing the apex, where the
      // legs genuinely converge) so chart distances stay monotonic; large
      // jumps were already rejected by the corridor.
      const matched = refDistances[closestIndex]
      // A fresh match (it moved off the previous reference point) resets the
      // accumulator; staying on the exact same reference distance keeps it
      // growing so the corridor can widen forward across a reference gap.
      if (anchor === undefined || matched !== anchor) {
        travelSinceAnchor = 0
      }
      anchor = matched
      const along = distanceAlongReference(
        tLat,
        tLon,
        closestIndex,
        refLats,
        refLons,
        refDistances
      )
      result[t] = t > 0 && along < result[t - 1] ? result[t - 1] : along
      lastIndex = closestIndex
    }
  }

  return result
}

/**
 * Recalibrate all points from a target lap to match a reference lap's distance scale.
 *
 * This uses GPS coordinates to find geographically closest points between laps,
 * ensuring that the same physical location on the track has the same distance value
 * regardless of which lap/session it came from.
 *
 * @param targetPoints - GPS points from the lap to recalibrate
 * @param referencePoints - GPS points from the reference lap
 * @returns Array of recalibrated points with both original and aligned distances
 */
export function recalibratePointsToReference(
  targetPoints: GpsPoint[],
  referencePoints: GpsPoint[]
): RecalibratedPoint[] {
  const recalibrate = createDistanceRecalibrator(referencePoints)
  let lastIndex = 0

  return targetPoints.map((point) => {
    const { recalibratedDistance, newIndex } = recalibrate(point, lastIndex)
    lastIndex = newIndex

    return {
      originalPoint: point,
      recalibratedDistance,
      originalDistance: point.distanceMeters,
    }
  })
}
